import axios from "axios";
import { getStoredToken, clearAuthData } from "../utils/tokenUtils";
import {
  API_BASE_PREFIXED,
  addFallbackInterceptor,
} from "../config/api.config";
import { API } from "../config/api.paths";
import { JSEncrypt } from "jsencrypt";
import { installApiLoadingInterceptors } from "../utils/apiLoading";
import { injectClientIdIntoConfig } from "../utils/clientContext";
const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAx2ZvNe0OFpJIws3REIHN
FnniLPT8d2KXS1MgpW42xIj1RnZaWIP5KdztcinAP0RsQ4g8er1+zqtiLQwGz0Il
2kUtLyD983+JNih6cpZSDmYe+ztMPeEm2Z1VpJWaccLzoxYCEwbOjHZ9ZjoyykVP
rjNl1dZ+9O4d1zloLe+tfbbe+SQaMAvX/lAofSWKv89/IKl1BCKxoB0wANhk6F+2
NkrnkEyYOKDVe6O6vfj4GAuVEkgMfsRv9Z74EpNefcuIqHzj9ycDV5gr4cyJ1NmL
28kODLXy62SsrGK1uwZWKx6NA5zQe5Orbgr67KnawvRGMYgumvzS/PDXT/N0CmNf
UwIDAQAB
-----END PUBLIC KEY-----
`;

// Use prefixed URLs (new standard)
const API_BASE_URL = API_BASE_PREFIXED.backend;
const USER_SERVICE_URL = API_BASE_PREFIXED.user;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// Add fallback interceptor for backward compatibility
addFallbackInterceptor(
  api,
  API_BASE_PREFIXED.backend,
  process.env.REACT_APP_API_URL || "http://localhost:3000",
);

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token using utility function (includes expiry check)
    const token = getStoredToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add additional headers to prevent 431 errors
    config.headers["Accept"] = "application/json";
    config.headers["Cache-Control"] = "no-cache";

    return injectClientIdIntoConfig(config);
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 errors for authenticated endpoints, not login endpoints
    if (error.response?.status === 401 && !isLoginEndpoint(error.config?.url)) {
      // Clear auth data using utility function
      clearAuthData();

      // Show session expired popup
      showSessionExpiredPopup();
    }
    return Promise.reject(error);
  },
);

// Helper function to check if the request is to a login endpoint
const isLoginEndpoint = (url) => {
  if (!url) return false;
  const loginEndpoints = [
    "user/login/send-otp",
    "user/login/verify-otp",
    "user/auth/login",
    "user/auth/mfa/setup/init",
    "user/auth/mfa/setup/confirm",
    "user/register",
    "user/api/auth/signup",
    "user/forgot-password/send-otp",
    "user/forgot-password/verify-otp",
    "user/forgot-password/reset",
  ];
  return loginEndpoints.some((endpoint) => url.includes(endpoint));
};

// Function to show session expired popup
const showSessionExpiredPopup = () => {
  // Check if popup already exists
  if (document.getElementById("session-expired-popup")) {
    return;
  }

  // Define handlers BEFORE creating the popup to ensure they're available
  const handleLoginRedirect = () => {
    // Remove popup
    const popupElement = document.getElementById("session-expired-popup");
    if (popupElement) {
      document.body.removeChild(popupElement);
    }
    // Restore body scroll
    document.body.style.overflow = "";
    // Redirect to login page
    window.location.href = "/login";
  };

  const closeSessionPopup = () => {
    const popupElement = document.getElementById("session-expired-popup");
    if (popupElement) {
      document.body.removeChild(popupElement);
    }
    document.body.style.overflow = "";
  };

  // Store handlers globally for inline onclick handlers
  window.handleLoginRedirect = handleLoginRedirect;
  window.closeSessionPopup = closeSessionPopup;

  // Create popup element
  const popup = document.createElement("div");
  popup.id = "session-expired-popup";
  popup.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    pointer-events: auto;
  `;

  const modalContent = document.createElement("div");
  modalContent.style.cssText = `
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    max-width: 400px;
    text-align: center;
    position: relative;
    pointer-events: auto;
    z-index: 100000;
  `;

  // Close button (X)
  const closeBtn = document.createElement("button");
  closeBtn.innerHTML = "&times;";
  closeBtn.style.cssText = `
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #6b7280;
    padding: 5px;
    line-height: 1;
    pointer-events: auto;
  `;
  closeBtn.onclick = closeSessionPopup;
  closeBtn.type = "button";

  // Error icon
  const iconDiv = document.createElement("div");
  iconDiv.style.cssText = `
    width: 48px;
    height: 48px;
    background: #ef4444;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
  `;
  iconDiv.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="15" y1="9" x2="9" y2="15"></line>
      <line x1="9" y1="9" x2="15" y2="15"></line>
    </svg>
  `;

  // Title
  const title = document.createElement("h3");
  title.textContent = "Session Expired";
  title.style.cssText = `
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  `;

  // Message
  const message = document.createElement("p");
  message.textContent =
    "Your session has expired. You need to login again to continue.";
  message.style.cssText = `
    margin: 0 0 1.5rem;
    color: #6b7280;
    line-height: 1.5;
  `;

  // Buttons container
  const buttonsDiv = document.createElement("div");
  buttonsDiv.style.cssText =
    "display: flex; gap: 10px; justify-content: center;";

  // Cancel button
  const cancelBtn = document.createElement("button");
  cancelBtn.textContent = "Cancel";
  cancelBtn.type = "button";
  cancelBtn.style.cssText = `
    background: #6b7280;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    pointer-events: auto;
  `;
  cancelBtn.onclick = closeSessionPopup;
  cancelBtn.onmouseover = () => {
    cancelBtn.style.background = "#4b5563";
  };
  cancelBtn.onmouseout = () => {
    cancelBtn.style.background = "#6b7280";
  };

  // Login Again button
  const loginBtn = document.createElement("button");
  loginBtn.textContent = "Login Again";
  loginBtn.type = "button";
  loginBtn.style.cssText = `
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    pointer-events: auto;
  `;
  loginBtn.onclick = handleLoginRedirect;
  loginBtn.onmouseover = () => {
    loginBtn.style.background = "#2563eb";
  };
  loginBtn.onmouseout = () => {
    loginBtn.style.background = "#3b82f6";
  };

  // Assemble modal
  buttonsDiv.appendChild(cancelBtn);
  buttonsDiv.appendChild(loginBtn);
  modalContent.appendChild(closeBtn);
  modalContent.appendChild(iconDiv);
  modalContent.appendChild(title);
  modalContent.appendChild(message);
  modalContent.appendChild(buttonsDiv);
  popup.appendChild(modalContent);

  // Add popup to page
  document.body.appendChild(popup);

  // Prevent body scroll
  document.body.style.overflow = "hidden";

  // Also prevent clicks on backdrop from closing (only buttons should work)
  popup.addEventListener("click", (e) => {
    if (e.target === popup) {
      // Clicked on backdrop, do nothing (or optionally close)
      // e.stopPropagation();
    }
  });
};

// Create separate axios instance for user service
const userServiceAPI = axios.create({
  baseURL: USER_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// Add fallback interceptor for backward compatibility
addFallbackInterceptor(
  userServiceAPI,
  API_BASE_PREFIXED.user,
  process.env.REACT_APP_USER_SERVICE_URL || "http://localhost:3001",
);

// Add auth token to user service requests
userServiceAPI.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Accept"] = "application/json";
    config.headers["Cache-Control"] = "no-cache";
    return config;
  },
  (error) => Promise.reject(error),
);

const encryptData = (data) => {
  const crypt = new JSEncrypt();
  crypt.setPublicKey(publicKey);
  const encrypted = crypt.encrypt(JSON.stringify(data));
  return encrypted;
};
// Auth API calls - now using semantic paths
export const authAPI = {
  // Login step 1: Send credentials and get OTP
  login: async (email, password) => {
    const encryptedData = encryptData({ email, password });
    const response = await userServiceAPI.post(API.user.auth.login.otp.send(), {
      data: encryptedData,
    });
    return response.data;
  },

  // Login step 2: Verify OTP and get token (may return requires_mfa + mfa_temp_token)
  verifyOTP: async (email, otp) => {
    const response = await userServiceAPI.post(
      API.user.auth.login.otp.verify(),
      { email, otp },
    );
    return response.data;
  },

  // Login step 3: Verify MFA code when client has MFA enabled
  verifyMfa: async (mfaTempToken, code) => {
    const response = await userServiceAPI.post(
      API.user.auth.login.verifyMfa(),
      { mfa_temp_token: mfaTempToken, code },
    );
    return response.data;
  },

  // Public MFA setup (no auth)
  mfaSetupInit: async (email, password) => {
    const response = await userServiceAPI.post(API.user.auth.mfaSetup.init(), {
      email,
      password,
    });
    return response.data;
  },
  mfaSetupConfirm: async (mfaSetupToken, code) => {
    const response = await userServiceAPI.post(
      API.user.auth.mfaSetup.confirm(),
      { mfa_setup_token: mfaSetupToken, code },
    );
    return response.data;
  },

  // Register new user (admin-created)
  register: async (userData) => {
    const response = await userServiceAPI.post(
      API.user.auth.register(),
      userData,
    );
    return response.data;
  },

  // Self-signup with client auto-creation
  signup: async (signupData) => {
    const response = await userServiceAPI.post(
      API.user.auth.signup(),
      signupData,
    );
    return response.data;
  },

  // Get user profile
  getProfile: async () => {
    const response = await userServiceAPI.post(API.user.profile.me());
    return response.data;
  },

  // MFA (requires auth)
  getMfaStatus: async () => {
    const response = await userServiceAPI.get(API.user.mfa.status());
    return response.data;
  },
  mfaSetupStart: async () => {
    const response = await userServiceAPI.post(API.user.mfa.setup());
    return response.data;
  },
  mfaSetupVerify: async (code) => {
    const response = await userServiceAPI.post(API.user.mfa.verifySetup(), {
      code,
    });
    return response.data;
  },
  mfaDisable: async () => {
    const response = await userServiceAPI.post(API.user.mfa.disable());
    return response.data;
  },

  // Logout user
  logout: async () => {
    const response = await userServiceAPI.post(API.user.auth.logout());
    return response.data;
  },

  // Validate token
  validateToken: async () => {
    const response = await userServiceAPI.post(API.user.auth.token.validate());
    return response.data;
  },

  createDeviceAuthRequest: async (payload = {}) => {
    const response = await userServiceAPI.post(API.user.auth.device.request(), payload);
    return response.data;
  },
  getDeviceAuthRequestDetails: async (requestId) => {
    const response = await userServiceAPI.get(API.user.auth.device.details(requestId));
    return response.data;
  },
  getDeviceAuthRequestStatus: async (requestId) => {
    const response = await userServiceAPI.get(API.user.auth.device.status(requestId));
    return response.data;
  },
  approveDeviceAuthRequest: async (requestId) => {
    const response = await userServiceAPI.post(API.user.auth.device.approve(requestId), {});
    return response.data;
  },
  rejectDeviceAuthRequest: async (requestId, reason = "Rejected by user") => {
    const response = await userServiceAPI.post(API.user.auth.device.reject(requestId), { reason });
    return response.data;
  },

  // Google SSO - Initiate OAuth
  initiateGoogleOAuth: async (clientId = null) => {
    const response = await userServiceAPI.post("/api/auth/google/initiate", {
      client_id: clientId,
    });
    return response.data;
  },

  // Google SSO - Verify token (alternative flow)
  verifyGoogleToken: async (idToken, state) => {
    const response = await userServiceAPI.post("/api/auth/google/verify", {
      id_token: idToken,
      state,
    });
    return response.data;
  },
};

// Forgot Password API calls - now using user service (prefixed)
export const sendForgotPasswordOTP = async (email) => {
  const response = await userServiceAPI.post("/forgot-password/send-otp", {
    email,
  });
  return response.data;
};

export const verifyForgotPasswordOTP = async (email, otp, newPassword) => {
  const response = await userServiceAPI.post("/forgot-password/reset", {
    email,
    otp,
    new_password: newPassword,
  });
  return response.data;
};

export const verifyForgotPasswordCode = async (email, otp) => {
  const response = await userServiceAPI.post("/forgot-password/verify-otp", {
    email,
    otp,
  });
  return response.data;
};

// User Profile API calls - now using semantic paths
export const getUserProfile = async () => {
  const response = await userServiceAPI.post(API.user.profile.me());
  return response.data;
};

export const changePassword = async (
  currentPassword,
  newPassword,
  isFirstTime = false,
) => {
  const response = await userServiceAPI.post("/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
    is_first_time: isFirstTime,
  });
  return response.data;
};

// Health check API (uses semantic paths)
export const healthAPI = {
  checkHealth: async () => {
    const response = await api.post(API.backend.health.check());
    return response.data;
  },

  checkDBHealth: async () => {
    const response = await api.post(API.backend.health.db());
    return response.data;
  },
};

installApiLoadingInterceptors(api);
installApiLoadingInterceptors(userServiceAPI);

export { userServiceAPI };
export default api;
